import { IconNames } from '@dixa/lib-shared-habla';
import { Channel } from 'lib-shared-types-channels';

import { AIImproveWritingPromptName } from 'apis/ai';
import { CommandNameInternal } from 'utils/commands';

/** A copilot action that can be pinned to the editor toolbar. */
export type CopilotActionId =
  | 'improveReply'
  | 'improveReplyMatchStyleGuide'
  | 'translateToConversationLanguage'
  | 'translate'
  | 'checkSpellingAndGrammar'
  | 'expandText'
  | 'quickAiDraft'
  | 'customAiDraft';

/**
 * How an action produces its result. `promptName` and `includeContext` are
 * omitted when the caller supplies them per invocation, as improveReply does.
 */
export type CopilotExecution =
  | {
      kind: 'writingImprovement';
      promptName?: AIImproveWritingPromptName;
      includeContext?: boolean;
    }
  | { kind: 'translateToConversationLanguage' }
  | { kind: 'spellingGrammar' }
  | { kind: 'agentDraft' }
  | { kind: 'delegateToMenu' };

/** Values available to copy that varies at runtime. */
export type CopilotCopyContext = {
  languageName?: string;
};

export type CopilotCopy = string | ((context: CopilotCopyContext) => string);

/**
 * An improvement that is unavailable right now but could become available, so
 * it stays visible with the reason. Permanently inapplicable improvements are
 * excluded from the context instead, not disabled.
 */
export type CopilotDisabledImprovement = {
  value: AIImproveWritingPromptName;
  tooltip: string;
};

/**
 * The bag each action's `getStatus` destructures what it needs from. The
 * existing helpers in `utils/copilot` take four different argument shapes, so
 * entries adapt this to their helper rather than sharing a signature.
 */
export type CopilotStatusArgs = {
  /** Live editor text, for disabled state. Not the execution input. */
  text: string;
  /** The action's resolved name, used as the idle tooltip. */
  name: string;
  isLoading?: boolean;
  channel?: Channel;
  hasStyleGuideForChannel?: boolean;
  /** Whether the conversation language could be resolved. */
  hasLanguage?: boolean;
  /** Whether a default AI agent is set. */
  hasDefaultDraftAgent?: boolean;
  /** Whether the conversation holds an attachment Mim cannot read. */
  hasUnsupportedAttachment?: boolean;
  csid?: string;
};

export type CopilotStatus = {
  isDisabled: boolean;
  tooltipContent: string;
};

export type CopilotActionDefinition = {
  id: CopilotActionId;
  icon: IconNames;
  /** Wording for the copilot menu item. */
  name: string;
  /** Wording for the toolbar button. */
  shortName: string;
  commandName: CommandNameInternal;
  execution: CopilotExecution;
  insert: 'replace' | 'insertBelow';
  /** Whether the action is pinned to the toolbar until the user says otherwise. */
  defaultPinned: boolean;
  /**
   * Whether the dropdown menu item is disabled from the action's status.
   * Interactive panels handle their own empty-input state instead.
   */
  gateDropdownOnStatus?: boolean;
  /** Required for every execution kind except `delegateToMenu`. */
  loadingText?: CopilotCopy;
  /** Required for every execution kind except `delegateToMenu`. */
  errorText?: CopilotCopy;
  getStatus?: (args: CopilotStatusArgs) => CopilotStatus;
  featureFlag?: 'mimDrafts';
};

/** An improvement offered by a context, with any live reason it cannot run. */
export type CopilotOfferedImprovement = {
  value: AIImproveWritingPromptName;
  name: string;
  includeContext: boolean;
  /** Set when it is temporarily unavailable; shown disabled with this reason. */
  disabledReason?: string;
};

/** Which actions an editor context offers, and any wording overrides. */
export type CopilotActionContext = {
  actions: CopilotActionId[];
  names?: Partial<Record<CopilotActionId, string>>;
  /**
   * Improvements that can never apply in this context, so the toolbar's compact
   * menu hides them. `match_tone` needs an inbound customer message, so it is
   * excluded wherever there is none.
   */
  excludedImprovements?: AIImproveWritingPromptName[];
  /**
   * The same improvements in the shape the dropdown panel wants, which shows
   * them disabled with the reason rather than hiding them.
   */
  disabledImprovements?: CopilotDisabledImprovement[];
};

export const resolveCopy = (
  copy: CopilotCopy | undefined,
  context: CopilotCopyContext = {},
): string => {
  if (typeof copy === 'function') {
    return copy(context);
  }
  return copy ?? '';
};
